import React, { useState, useMemo } from 'react';
import { 
    Typography, 
    TextField, 
    InputAdornment, 
    IconButton, 
    Badge,
    FormControl,
    InputLabel,
    Select,
    Input,
    Divider
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AssessmentIcon from '@material-ui/icons/Assessment';
import FuseAnimate from '@fuse/core/FuseAnimate/FuseAnimate';
import DateRangePickerOmni from 'allGlobalComponents/DateRangePickerOmni';
import ButtonOmni from 'allGlobalComponents/ButtonOmni';
import ModalOmni from 'allGlobalComponents/ModalOmni';
import PageHeading from 'allGlobalComponents/PageHeading';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Box } from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';

const useStyles = makeStyles(theme => ({
    datePickerContainer: {
        position: 'relative',
        zIndex: theme.zIndex.modal + 1
    },
    headerSection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '0 20px',
        marginBottom: '16px',
        gap: '16px'
    },
    leftSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    filterContainer: {
        padding: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
        minHeight: '200px'
    },
    buttonContainer: {
        width: "100%",
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(2),
        gap: theme.spacing(2)
    },
    buttonGroup: {
        display: 'flex',
        gap: theme.spacing(2)
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary
    },
    formControl: {
        width: '100%'
    },
    menuPaper: {
        maxHeight: '400px',
        maxWidth: '536px',
        width: '536px',
        overflowY: 'auto'
    },
    searchBox: {
        padding: theme.spacing(2),
        position: 'sticky',
        top: 0,
        backgroundColor: theme.palette.background.paper,
        zIndex: 1,
        borderBottom: `1px solid ${theme.palette.divider}`
    },
    accordionRoot: {
        boxShadow: 'none',
        margin: '0 !important',
        '&:before': {
            display: 'none'
        }
    },
    accordionSummary: {
        minHeight: '48px !important',
        padding: '0 16px',
        '& .MuiAccordionSummary-content': {
            margin: '12px 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%'
        },
        '& .MuiAccordionSummary-expandIcon': {
            padding: '8px'
        }
    },
    accordionDetails: {
        padding: theme.spacing(0, 2, 2),
        display: 'block',
        fontSize: '13px',
        color: theme.palette.text.secondary,
        lineHeight: 1.5,
        wordBreak: 'break-word'
    },
    templateName: {
        fontWeight: 500,
        fontSize: '14px',
        color: theme.palette.text.primary,
        cursor: 'pointer',
        flex: 1,
        '&:hover': {
            color: theme.palette.primary.main
        }
    },
    noResults: {
        padding: theme.spacing(3),
        textAlign: 'center',
        color: theme.palette.text.secondary
    }
}));

function TemplateAnalyticsHeader({
    dateRange,
    onDateChange,
    templatesData = [],
    loadingTemplates = false,
    selectedTemplateId,
    onTemplateFilterChange
}) {
    const classes = useStyles();
    const [filterDialogOpen, setFilterDialogOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [tempSelectedTemplateId, setTempSelectedTemplateId] = useState(selectedTemplateId);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedTemplateName, setSelectedTemplateName] = useState('');
    const [expandedAccordion, setExpandedAccordion] = useState(null);

    // Filter and sort templates
    const filteredTemplates = useMemo(() => {
        return templatesData
            .filter(msg => {
                if (!searchQuery) return true;
                const searchLower = searchQuery.toLowerCase();
                const templateName = msg?.template_name?.toLowerCase() || '';
                const templateId = msg?.id?.toString() || '';
                const bodyText = msg?.message_body?.template?.components
                    ?.filter(x => x.type === 'BODY')[0]?.text?.toLowerCase() || '';
                return (
                    templateName.includes(searchLower) ||
                    templateId.includes(searchLower) ||
                    bodyText.includes(searchLower)
                );
            })
            .sort((a, b) => {
                const nameA = a?.template_name?.toLowerCase() || '';
                const nameB = b?.template_name?.toLowerCase() || '';
                return nameA.localeCompare(nameB);
            });
    }, [templatesData, searchQuery]);

    // Get active filter count
    const getActiveFilterCount = () => {
        return selectedTemplateId ? 1 : 0;
    };

    const calculatedFilterCount = getActiveFilterCount();

    // Get selected template name
    const getSelectedTemplateName = (id) => {
        const template = templatesData.find(t => t.id === id);
        return template ? `(${template.id}) ${template.template_name}` : '';
    };

    // Open filter dialog
    const handleOpenFilterDialog = () => {
        setTempSelectedTemplateId(selectedTemplateId);
        setSelectedTemplateName(selectedTemplateId ? getSelectedTemplateName(selectedTemplateId) : '');
        setSearchQuery('');
        setExpandedAccordion(null);
        setFilterDialogOpen(true);
    };

    // Apply filters
    const handleApplyFilters = () => {
        onTemplateFilterChange(tempSelectedTemplateId);
        setFilterDialogOpen(false);
    };

    // Clear filters
    const handleClearFilters = () => {
        setTempSelectedTemplateId(null);
        setSelectedTemplateName('');
        onTemplateFilterChange(null);
        setFilterDialogOpen(false);
    };

    // Cancel filter changes
    const handleCancelFilters = () => {
        setTempSelectedTemplateId(selectedTemplateId);
        setSelectedTemplateName(selectedTemplateId ? getSelectedTemplateName(selectedTemplateId) : '');
        setFilterDialogOpen(false);
    };

    // Handle dropdown close
    const handleDropdownClose = () => {
        setDropdownOpen(false);
        setSearchQuery('');
        setExpandedAccordion(null);
    };

    // Handle dropdown open
    const handleDropdownOpen = () => {
        setDropdownOpen(true);
    };

    // Handle template selection
    const handleTemplateSelect = (template) => {
        setTempSelectedTemplateId(template.id);
        setSelectedTemplateName(`(${template.id}) ${template.template_name}`);
        handleDropdownClose();
    };

    // Clear selected template
    const handleClearSelectedTemplate = () => {
        setTempSelectedTemplateId(null);
        setSelectedTemplateName('');
    };

    // Handle accordion expand
    const handleAccordionChange = (templateId) => (event, isExpanded) => {
        event.stopPropagation();
        setExpandedAccordion(isExpanded ? templateId : null);
    };

    // Filter dialog content
    const filterDialogContent = (
        <div className={classes.filterContainer}>
            <Typography className={classes.sectionTitle}>
                Select Template
            </Typography>

            <FormControl className={classes.formControl}>
                <InputLabel style={{ marginLeft: 10 }}>Templates</InputLabel>
                {selectedTemplateName ? (
                    <Input
                        disabled={true}
                        value={selectedTemplateName}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    onClick={handleClearSelectedTemplate}
                                >
                                    <CloseIcon style={{ width: '15px', color: 'grey' }} />
                                </IconButton>
                            </InputAdornment>
                        }
                    />
                ) : (
                    <Select
                        open={dropdownOpen}
                        onClose={handleDropdownClose}
                        onOpen={handleDropdownOpen}
                        value=""
                        MenuProps={{
                            classes: { paper: classes.menuPaper },
                            autoFocus: false,
                            disableAutoFocusItem: true,
                            anchorOrigin: {
                                vertical: 'bottom',
                                horizontal: 'left'
                            },
                            transformOrigin: {
                                vertical: 'top',
                                horizontal: 'left'
                            },
                            getContentAnchorEl: null
                        }}
                    >
                        <div className={classes.searchBox}>
                            <TextField
                                fullWidth
                                size="small"
                                variant="outlined"
                                placeholder="Search by ID, name, or text..."
                                value={searchQuery}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    setSearchQuery(e.target.value);
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                autoFocus={false}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchQuery && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSearchQuery('');
                                                }}
                                            >
                                                <CloseIcon style={{ fontSize: '18px' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </div>
                        {loadingTemplates ? (
                            <div className={classes.noResults}>Loading templates...</div>
                        ) : filteredTemplates.length > 0 ? (
                            filteredTemplates.map((template, index) => {
                                const bodyComp = template?.message_body?.template?.components?.filter(
                                    x => x.type === 'BODY'
                                );
                                const bodyText = bodyComp?.[0]?.text || 'No body text';

                                return (
                                    <div key={template.id}>
                                        <Accordion
                                            classes={{ root: classes.accordionRoot }}
                                            expanded={expandedAccordion === template.id}
                                            onChange={handleAccordionChange(template.id)}
                                        >
                                            <AccordionSummary
                                                className={classes.accordionSummary}
                                                expandIcon={<ExpandMoreIcon />}
                                            >
                                                <Typography 
                                                    className={classes.templateName}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleTemplateSelect(template);
                                                    }}
                                                >
                                                    ({template.id}) {template.template_name}
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails className={classes.accordionDetails}>
                                                {bodyText}
                                            </AccordionDetails>
                                        </Accordion>
                                        {index !== filteredTemplates.length - 1 && <Divider />}
                                    </div>
                                );
                            })
                        ) : (
                            <div className={classes.noResults}>No templates found</div>
                        )}
                    </Select>
                )}
            </FormControl>
        </div>
    );

    // Filter dialog actions
    const filterDialogActions = (
        <div className={classes.buttonContainer}>
            <div className={classes.buttonGroup}>
                <ButtonOmni onClick={handleClearFilters} size="small" variant="outlined" label="Clear" />
            </div>
            <div className={classes.buttonGroup}>
                <ButtonOmni onClick={handleCancelFilters} size="small" variant="outlined" label="Cancel" />
                <ButtonOmni onClick={handleApplyFilters} size="small" variant="outlined" label="Apply" />
            </div>
        </div>
    );

    return (
        <>
            <PageHeading
                icon={AssessmentIcon}
                title="Template Analytics"
                leftContent={
                    <div className={classes.datePickerContainer}>
                        <DateRangePickerOmni
                            startDate={dateRange.startDate}
                            endDate={dateRange.endDate}
                            onDateChange={onDateChange}
                            color="black"
                        />
                    </div>
                }
                rightContent={
                    calculatedFilterCount > 0 ? (
                        <Badge badgeContent={calculatedFilterCount} color="primary">
                            <ButtonOmni
                                loading={false}
                                onClick={handleOpenFilterDialog}
                                size="small"
                                variant="outlined"
                                label={
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <FilterListIcon />
                                        Filters
                                    </Box>
                                }
                            />
                        </Badge>
                    ) : (
                        <ButtonOmni
                            loading={false}
                            onClick={handleOpenFilterDialog}
                            size="small"
                            variant="outlined"
                            label={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <FilterListIcon />
                                    Filters
                                </Box>
                            }
                        />
                    )
                }
            />

            <ModalOmni
                open={filterDialogOpen}
                handleClose={() => setFilterDialogOpen(false)}
                title="Template Filters"
                content={filterDialogContent}
                actions={filterDialogActions}
                customWidth={600}
            />
        </>
    );
}

export default TemplateAnalyticsHeader;